import { AxiosError } from 'axios'
import { ENDPOINT_PROJECT_SETTINGS } from '../config'
import type {
  DisableProjectAdvancedStatusRequest,
  ProjectStatusModeResponse,
} from '../types'
import AuthAxios from '@/lib/api/axios/AuthAxios'

/**
 * Disable advanced status management for a specific project.
 *
 * Backend: `POST /api/v1.0/e/{workspaceSlug}/projects/{projectId}/settings/disable-advanced-status-management`
 * Flips Project.StatusMode to "simple" and queues MigrateTaskStatusesJob which
 * reassigns this project's PL/IP/CL tasks to BL/CD per `request.reassignments`.
 * Returns `statusMigrationInProgress = true` until the background job finishes.
 *
 * Cycles must be disabled on the project first — the backend returns 400 otherwise.
 */
const disableProjectAdvancedStatus = async (
  workspaceSlug: string,
  projectId: string,
  request: DisableProjectAdvancedStatusRequest,
): Promise<ProjectStatusModeResponse> => {
  try {
    const response = await AuthAxios.post<ProjectStatusModeResponse>(
      `${ENDPOINT_PROJECT_SETTINGS}/${workspaceSlug}/projects/${projectId}/settings/disable-advanced-status-management`,
      request,
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to change status mode for this project')
      }
      if (error.response?.status === 400 && typeof error.response.data?.message === 'string') {
        throw new Error(error.response.data.message)
      }
    }
    throw error
  }
}

export default disableProjectAdvancedStatus
